import React, { useEffect } from "react"
import { useDispatch } from "react-redux"
import { initialize as initializeAnswerOptionsLists } from "reduxSlices/answerOptionsListsSlice"
import { loadCategoryFromSlug } from "reduxSlices/categorySlice"
import {
  initialize as initializeConversations,
  resetConversationsData,
} from "reduxSlices/formSubmissionConversationsSlice"
import { resetResolutionData } from "reduxSlices/formSubmissionResolutionSlice"
import { updateFormSubmission, resetData } from "reduxSlices/formSubmissionSlice"
import { loadGroups } from "reduxSlices/groupsSlice"
import { loadUsers } from "reduxSlices/organizationUsersSlice"
import { clearAllDraftAnswers } from "reduxSlices/taskDraftAnswersSlice"
import { FormSubmissionFormContextProvider } from "contexts/FormSubmissionFormContext"
import { formSubmissionShape, submissionPermissionsShape } from "utils/propTypeShapes"
import { ACTIVITY_HUB_SUBMISSIONS_PATH } from "utils/routeHelpers"
import * as API from "services/api"
import withReduxProvider from "shared/withReduxProvider"
import FormSubmissionTabs from "views/FormSubmission/FormSubmissionTabs"
import DetailsBanner from "./DetailsBanner"
import SyncAllTaskAnswers from "./SyncAllTaskAnswers"

const confirmDelete = () => window.confirm("Are you sure you want to delete this submission?")

const FormSubmissionPage = ({ formSubmission, submissionPermissions }) => {
  const dispatch = useDispatch()

  const { categorySlug, formSlug, formVersionId } = formSubmission
  const { canDeleteSubmission, canExportSubmission } = submissionPermissions

  // When the component unmounts
  // - Clear all draft answers
  // - Reset the form submission data in Redux
  useEffect(() => () => {
    dispatch(clearAllDraftAnswers())
    dispatch(resetConversationsData())
    dispatch(resetResolutionData())
    dispatch(resetData())
  }, [dispatch])

  // Load organization users when the component mounts
  useEffect(() => {
    const load = async () => {
      const response = await API.getUsers()

      if (response.ok) {
        return response.data
      }

      throw new Error("Unable to load users.")
    }

    dispatch(loadUsers({ load }))
  }, [dispatch])

  // Load organization groups when the component mounts
  useEffect(() => {
    const load = async () => {
      const response = await API.getGroups()

      if (response.ok) {
        return response.data
      }

      throw new Error("Unable to load groups.")
    }

    dispatch(loadGroups({ load }))
  }, [dispatch])

  useEffect(() => {
    dispatch(initializeAnswerOptionsLists({}))
  }, [dispatch])

  // Load category, need to load the category's submission statuses and task forms
  useEffect(() => {
    dispatch(loadCategoryFromSlug({ categorySlug, options: { loadSubmissionStatuses: true, loadTaskForms: true } }))
  }, [dispatch, categorySlug])

  // Update the form submission in Redux, which will load data associated with the form submission:
  // tasks, attachments, etc.
  useEffect(() => {
    dispatch(updateFormSubmission({ formSubmission, submissionPermissions }))
  }, [dispatch, formSubmission, submissionPermissions])

  // Load conversations when the form submission page mounts so they are
  // available before the Conversations tab is visited.
  useEffect(() => {
    dispatch(initializeConversations({ formSubmissionSlug: formSubmission.slug }))
  }, [dispatch, formSubmission])

  const deleteFormSubmission = async () => {
    if (confirmDelete()) {
      const response = await API.deleteFormSubmission({ slug: formSubmission.slug })

      if (response.ok) {
        window.location.pathname = ACTIVITY_HUB_SUBMISSIONS_PATH
      } else {
        console.error("Error deleting form submission: ", response)
      }
    }
  }

  const exportFormSubmissionData = (includeFormSubmission, includeConversations, includeResolution, includedTaskIds) => async () => {
    const response = await API.exportFormSubmissionData({
      slug: formSubmission.slug, includeFormSubmission, includeConversations, includeResolution, includedTaskIds,
    })

    if (response.ok) {
      // eslint-disable-next-line no-undef
      const blob = new Blob([response.data], { type: "application/pdf" })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement("a")

      link.href = url
      link.download = `${formSubmission.slug}.pdf`
      link.click()
      link.remove()
    } else {
      console.error("Error exporting form submission: ", response)
    }
  }

  return (
    <FormSubmissionFormContextProvider formSlug={formSlug} submittedFormVersionId={formVersionId}>
      <SyncAllTaskAnswers />
      <div className="relative flex flex-col min-h-[calc(100vh-57px)]">
        <DetailsBanner
          canDeleteSubmission={canDeleteSubmission}
          deleteFormSubmission={deleteFormSubmission}
          canExportSubmission={canExportSubmission}
          exportFormSubmissionData={exportFormSubmissionData}
        />
        <FormSubmissionTabs />
      </div>
    </FormSubmissionFormContextProvider>
  )
}

FormSubmissionPage.propTypes = {
  formSubmission: formSubmissionShape.isRequired,
  submissionPermissions: submissionPermissionsShape.isRequired,
}

export default withReduxProvider(FormSubmissionPage)
